import { LOCATION_MODE } from '@/types/location-mode';
import { QUOTA_PERIOD } from './quota-period';
import { BOOKING_STATUS } from './service';

export enum APPOINTMENT_STATUS {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW',
}

export enum APPOINTMENT_ORIGIN {
  CLIENT = 'CLIENT',
  PROVIDER = 'PROVIDER',
  RECURRENCE = 'RECURRENCE',
}

export type CancelledBy = 'CLIENT' | 'PROVIDER' | 'SYSTEM';

export interface Appointment {
  id: string;
  providerId: string;
  clientId: string;
  serviceId: string;
  enrollmentId: string | null;
  serviceName?: string;
  clientName?: string;
  /** Nome do negócio — a home do cliente mostra com quem é o horário. */
  providerName?: string | null;
  /** Endereço da sessão: o do cliente quando o prestador vai até ele, senão o do serviço. */
  address?: string | null;
  /** Link da chamada do atendimento online — nulo até o prestador enviar. */
  meetingUrl?: string | null;
  /** Onde acontece — decide mapa, "no seu endereço" ou online. Nulo = deriva do endereço. */
  locationMode?: LOCATION_MODE | null;
  price?: number | null;
  startAt: string;
  endAt: string;
  status: APPOINTMENT_STATUS;
  origin: APPOINTMENT_ORIGIN;
  isOverbook: boolean;
  cancelledBy: CancelledBy | null;
  cancelReason: string | null;
  createdAt: string;
}

export interface BookableProvider {
  id: string;
  name: string;
  slug: string | null;
  logoUrl: string | null;
  brandColor: string | null;
  category: string | null;
}

export interface BookableServiceDetail {
  id: string;
  name: string;
  description: string;
  price: number | null;
  priceDisplayMode: 'show' | 'from' | 'hidden' | string;
  durationMinutes: number | null;
  slotCapacity: number;
  address: string | null;
  /** Onde acontece — pergunta o endereço quando o profissional vai até o cliente. */
  locationMode?: LOCATION_MODE | null;
  providerId: string;
  providerName: string;
  brandColor: string | null;
  coverUrl: string | null;
  /** Fotos do serviço (máx. 3), ordenadas — carrossel de capa. */
  photos: string[];
  /** Se o cliente consegue marcar sozinho, e por quê quando não consegue. */
  bookingStatus: BOOKING_STATUS;
  /** Assinatura: a tela não oferece sessão avulsa, só a agenda que se repete. */
  isSubscription?: boolean;
  /**
   * Quantas sessões por período a assinatura dá. É a presença deste bloco
   * que faz a tela trocar "montar a agenda" por "assinar e marcar sessão a
   * sessão".
   */
  quotaTerms?: {
    sessions: number;
    period: QUOTA_PERIOD;
  } | null;
  recurringBookingMode?: 'AUTO' | 'REQUEST' | 'OFF';
  /**
   * Termos da recorrência. Dias e horários não vêm daqui: o cliente monta a
   * agenda a partir da disponibilidade real do prestador.
   */
  recurringTerms?: {
    billingInterval: string | null;
    horizonDays: number;
    maxHorizonDays: number;
  } | null;
  /**
   * Página pública de organização `TEAM`: todas as cópias ativas do serviço
   * (esta incluída), na ordem da equipe. Com 2+ a tela pergunta "Com quem?".
   */
  professionals?: {
    providerId: string;
    serviceId: string;
    name: string;
    image: string | null;
  }[];
}

export interface BookableService {
  id: string;
  name: string;
  durationMinutes: number;
  slotCapacity: number;
  address: string | null;
}

export interface AvailabilitySlot {
  startAt: string;
  endAt: string;
  remaining: number;
  /**
   * Disponibilidade da equipe ("qualquer profissional"): quem está livre neste
   * horário, com a cópia do serviço de cada um. Ausente fora desse modo.
   */
  professionals?: { providerId: string; serviceId: string }[];
}

export interface AvailabilityDay {
  date: string;
  available: boolean;
  slots: AvailabilitySlot[];
}

/**
 * Por que não há horário para oferecer. Vem sempre junto com uma lista vazia —
 * a tela nunca precisa adivinhar entre "prestador não configurou" e "não sobrou
 * vaga no período".
 */
export type UnavailableReason = 'CUSTOM_DURATION' | 'NO_GRID' | 'PAUSED' | 'NO_SLOTS_IN_RANGE';

export interface AvailabilityResult {
  enabled: boolean;
  reason?: UnavailableReason;
  service?: {
    id: string;
    name: string;
    durationMinutes: number;
    slotCapacity: number;
  };
  days: AvailabilityDay[];
  nextAvailableAt: string | null;
}

export interface CreateAppointmentDto {
  serviceId: string;
  startAt: string;
  /** Endereço do cliente — só em serviço em que o profissional vai até ele. */
  address?: string;
}

export interface RescheduleAppointmentDto {
  startAt: string;
}

// --- Configuração do prestador ---

/**
 * Ajustes finos da agenda. Nunca pré-requisito: a agenda está sempre ativa,
 * quem decide se há horário é a grade, e todo agendamento espera aprovação.
 */
export interface ScheduleSettings {
  minBookingLeadMinutes: number;
  minCancelLeadMinutes: number;
  bookingHorizonDays: number;
  maxFuturePerClient: number;
  reminderEnabled: boolean;
  reminderLeadMinutes: number;
  noShowAdjustDays: number;
  slotStepMinutes: number;
  timezone: string;
}

export interface AvailabilityInterval {
  weekday: number;
  startTime: string;
  endTime: string;
}

export interface AvailabilityBlock {
  id: string;
  providerId: string;
  startAt: string;
  /** Nulo = pausa sem data para voltar. */
  endAt: string | null;
  allDay: boolean;
  reason?: string | null;
}
